// The tenancy page's "Moving in" / "Moving out" panel (Task F; prototype §11 clCardHTML): the
// checklist's stages as accordions, each item with its tick, its values (meter readings, keys,
// clerk, dates), who ticked it and when, and — for the items that reach further — what ticking
// does (activates / ends the tenancy, books an inspection, raises a work order). A completed
// move-in shows the closing email and lets it be sent.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Lock, Mail, KeyRound, Ban, FileText, Upload as UploadIcon } from 'lucide-react';
import { Badge, Button, Input, Select, SkeletonRows } from '../ui';
import { useCan } from '../../hooks/usePermission';
import { formatDate, formatMoney } from '../../lib/viz';
import { checklistsApi, EFFECT_NOTE, refUrl, refFileUrl, type Checklist, type ChecklistItem, type ChecklistKind } from '../../lib/checklists';
import { fileToUpload, openStoredFile } from '../../lib/reservations';
import { ApiRequestError } from '../../lib/api';

const LOCKING = ['ACTIVATE_TENANCY', 'END_TENANCY', 'BOOK_INSPECTION', 'CLEANING_WORK_ORDER', 'WORKS_WORK_ORDER'];
const when = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function dueText(c: Checklist) {
  if (c.kind === 'MOVE_OUT' && c.tenancy.status === 'ENDED' && c.tenancy.moveOutDate) return `Keys back — moved out ${formatDate(c.tenancy.moveOutDate)}`;
  if (!c.targetDate || c.daysToTarget === null) return null;
  const what = c.kind === 'MOVE_IN' ? 'Moves in' : 'Tenancy ends';
  const d = c.daysToTarget;
  const rel = d === 0 ? 'today' : d < 0 ? `${-d} day${d === -1 ? '' : 's'} ago` : `in ${d} day${d === 1 ? '' : 's'}`;
  return `${what} ${formatDate(c.targetDate)} · ${rel}`;
}

export function ChecklistPanel({ tenancyId, kind }: { tenancyId: string; kind: ChecklistKind }) {
  const qc = useQueryClient();
  const canTick = useCan('checklists.update');
  const canComplete = useCan('checklists.complete');
  const key = ['/checklists', tenancyId, kind];
  const q = useQuery({ queryKey: key, queryFn: () => checklistsApi.get(tenancyId, kind) });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const c = q.data;

  // Open the stage holding the next item the first time the list loads.
  useEffect(() => {
    if (!c || Object.keys(open).length) return;
    const next = c.stages.find((s) => s.done < s.total);
    if (next) setOpen({ [next.key]: true });
  }, [c, open]);

  const refreshOthers = () => {
    void qc.invalidateQueries({ queryKey: ['tenancy', tenancyId] });
    void qc.invalidateQueries({ queryKey: ['tenancy-deposits', tenancyId] });
    void qc.invalidateQueries({ queryKey: ['/changeovers'] });
    void qc.invalidateQueries({ queryKey: ['tenancy-parking', tenancyId] });
    // The application drawer's Next line and documents (ID seen in person re-syncs Goodlord).
    void qc.invalidateQueries({ queryKey: ['/reservations'] });
    void qc.invalidateQueries({ queryKey: ['/reservations/applications'] });
  };
  const fail = (e: unknown) => setError(e instanceof ApiRequestError ? e.message : 'Something went wrong');
  const tick = useMutation({
    mutationFn: (v: { key: string; body: Parameters<typeof checklistsApi.tick>[2] }) => checklistsApi.tick(c!.id, v.key, v.body),
    onMutate: () => { setError(null); setFlash(null); },
    onSuccess: (next, v) => {
      qc.setQueryData(key, next);
      const item = next.stages.flatMap((s) => s.items).find((i) => i.key === v.key);
      if (item?.ref && v.body.done) setFlash(`${item.label}: ${item.ref.label}`);
      if (item?.effect && v.body.done) refreshOthers();
    },
    onError: fail,
  });
  const attach = useMutation({
    mutationFn: async (v: { key: string; file: File }) => checklistsApi.attachDocument(c!.id, v.key, await fileToUpload(v.file)),
    onMutate: () => { setError(null); setFlash(null); },
    onSuccess: (next) => { qc.setQueryData(key, next); refreshOthers(); },
    onError: fail,
  });
  const complete = useMutation({
    mutationFn: () => checklistsApi.complete(c!.id),
    onMutate: () => { setError(null); setFlash(null); },
    onSuccess: (r) => {
      qc.setQueryData(key, r.checklist); refreshOthers(); void qc.invalidateQueries({ queryKey: ['/checklists/closing-due'] });
      setFlash(r.checklist.kind === 'MOVE_IN'
        ? (r.letters.length ? `Complete. Welcome letter written to ${r.letters.map((l) => l.toEmail || 'a tenant with no email (draft)').join(', ')} — send it below.` : r.warning ?? 'Complete.')
        : 'Move-out complete.');
    },
    onError: fail,
  });
  const send = useMutation({
    mutationFn: () => checklistsApi.sendWelcome(c!.id),
    onMutate: () => { setError(null); setFlash(null); },
    onSuccess: (r) => {
      void q.refetch(); void qc.invalidateQueries({ queryKey: ['/checklists/closing-due'] });
      const failed = r.results.filter((x) => x.status !== 'SENT');
      setFlash(`${r.sent} welcome letter${r.sent === 1 ? '' : 's'} sent${failed.length ? ` · not sent: ${failed.map((f) => `${f.toEmail ?? 'no email'} (${f.error})`).join(', ')}` : ''}`);
    },
    onError: fail,
  });

  if (q.isLoading) return <SkeletonRows rows={5} cols={2} className="py-4" />;
  if (!c) {
    return (
      <div className="rounded-xl border border-ink-200 px-4 py-6 text-center text-[13px] text-ink-500">
        {kind === 'MOVE_OUT' ? 'No move-out yet. The checklist opens by itself when notice is given or an end date is set.' : 'No move-in checklist for this tenancy.'}
      </div>
    );
  }

  const due = dueText(c);
  const unsent = c.welcome.filter((w) => ['DRAFT', 'QUEUED', 'FAILED'].includes(w.status));
  const sent = c.welcome.filter((w) => w.status === 'SENT');
  return (
    <div className="space-y-3">
      {/* Header: where it is up to */}
      <div className="rounded-xl border border-ink-100 bg-white px-4 py-3.5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-semibold text-ink-900">{c.kind === 'MOVE_IN' ? 'Moving in' : 'Moving out'}</span>
              <Badge value={c.status === 'COMPLETE' ? 'COMPLETED' : c.overdue ? 'OVERDUE' : 'OPEN'} />
              <span className="text-[11px] text-ink-400">{c.region === 'AE' ? 'Dubai list' : 'England & Wales list'}</span>
            </div>
            {due && <div className={`mt-0.5 text-[12px] ${c.overdue && c.tenancy.status !== 'ENDED' ? 'font-medium text-red-600' : 'text-ink-500'}`}>{due}</div>}
            {c.status === 'OPEN' && c.nextItem && <div className="mt-0.5 text-[12px] text-ink-600">Next: <b>{c.nextItem.label}</b> <span className="text-ink-400">· {c.nextItem.stage}</span></div>}
            {c.status === 'COMPLETE' && <div className="mt-0.5 text-[12px] text-ink-500">Completed {c.completedAt ? when(c.completedAt) : ''}{c.completedByName ? ` by ${c.completedByName}` : ''}</div>}
          </div>
          {c.status === 'OPEN' && canComplete && (
            <Button size="sm" disabled={!c.allDone || complete.isPending} onClick={() => complete.mutate()} title={c.allDone ? undefined : `${c.outstanding} item(s) still to do`}>
              {c.kind === 'MOVE_IN' ? 'Complete & write welcome' : 'Complete move-out'}
            </Button>
          )}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink-100" role="progressbar" aria-valuenow={c.progress.percent} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full transition-all" style={{ width: `${c.progress.percent}%`, background: c.status === 'COMPLETE' ? 'var(--status-good)' : c.overdue ? 'var(--status-critical)' : 'var(--color-bronze-500)' }} />
          </div>
          <span className="shrink-0 text-[12px] tabular-nums text-ink-600">{c.progress.done} of {c.progress.total}</span>
        </div>
        {c.kind === 'MOVE_IN' && c.tenancy.inviteCode && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11.5px] text-ink-500">
            <KeyRound className="h-3.5 w-3.5 text-ink-300" />Resident invite code <span className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[12px] tracking-wider text-ink-800">{c.tenancy.inviteCode}</span>
            {c.tenancy.inviteExpiresAt ? <span>· valid to {formatDate(c.tenancy.inviteExpiresAt)}</span> : <span>· issued with the welcome letter</span>}
          </div>
        )}
      </div>

      {flash && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-800">{flash}</div>}
      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">{error}</div>}

      {/* The closing email (prototype §26) */}
      {c.kind === 'MOVE_IN' && c.status === 'COMPLETE' && (
        <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${sent.length && !unsent.length ? 'border-emerald-200 bg-emerald-50/60' : 'border-bronze-200 bg-bronze-50/60'}`}>
          <Mail className="h-4 w-4 shrink-0 text-bronze-600" />
          <div className="min-w-0 flex-1 text-[12.5px] text-ink-700">
            {unsent.length
              ? <><b>Write to the tenant.</b> Everything is ticked — the welcome email with their portal invite is waiting: {unsent.map((w) => `${w.toEmail || 'no email'} (${w.status.toLowerCase()})`).join(', ')}.</>
              : sent.length ? <>Welcome email sent {sent[0]!.sentAt ? when(sent[0]!.sentAt) : ''} to {sent.map((w) => w.toEmail).join(', ')}.</> : <>No welcome letter yet.</>}
          </div>
          {canComplete && (unsent.length > 0 || !sent.length) && <Button size="sm" icon="Send" disabled={send.isPending} onClick={() => send.mutate()}>{send.isPending ? 'Sending…' : 'Send'}</Button>}
          <Link to="/app/outbox" className="text-[12px] font-medium text-bronze-600 hover:underline">Outbox</Link>
        </div>
      )}

      {/* Deposit, read-only: the deposit module owns it */}
      {c.deposit && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-ink-100 bg-ink-50/60 px-3.5 py-2 text-[12px] text-ink-600">
          <span className="font-medium text-ink-800">Deposit</span>
          <span>{formatMoney(c.deposit.amountMinor, c.deposit.currency)}</span>
          <Badge value={c.deposit.status} />
          {c.deposit.scheme && <span>{c.deposit.scheme}{c.deposit.schemeReference ? ` ${c.deposit.schemeReference}` : ''}</span>}
          {c.deposit.status === 'HELD' && c.deposit.protectionDueBy && <span className={c.deposit.isOverdue ? 'font-medium text-red-600' : ''}>protect by {formatDate(c.deposit.protectionDueBy)}{c.deposit.isOverdue ? ' — late' : ''}</span>}
          <span className="text-ink-400">· managed on the Deposit card</span>
        </div>
      )}

      {/* Stages */}
      <div className="space-y-2">
        {c.stages.map((s) => {
          const isOpen = !!open[s.key];
          const complete = s.done === s.total;
          return (
            <div key={s.key} className="overflow-hidden rounded-xl border border-ink-100 bg-white">
              <button type="button" onClick={() => setOpen((o) => ({ ...o, [s.key]: !o[s.key] }))} className="flex w-full items-center gap-3 px-4 py-3 text-left">
                <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${complete ? 'bg-emerald-500 text-on-primary' : 'bg-ink-100 text-ink-500'}`}>{complete ? <Check className="h-3.5 w-3.5" /> : s.done}</span>
                <span className="min-w-0 flex-1 text-[13px] font-medium text-ink-800">{s.label}</span>
                <span className="text-[11.5px] tabular-nums text-ink-400">{s.done}/{s.total}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-ink-300 transition ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <ul className="divide-y divide-ink-50 border-t border-ink-100">
                  {s.items.map((i) => (
                    <ItemRow key={i.key} item={i} propertyId={c.propertyId} isNext={c.status === 'OPEN' && c.nextItem?.key === i.key} canTick={canTick} busy={tick.isPending && tick.variables?.key === i.key}
                      onTick={(body) => tick.mutate({ key: i.key, body })} uploading={attach.isPending && attach.variables?.key === i.key}
                      onUpload={(file) => attach.mutate({ key: i.key, file })} onError={fail} />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ItemRow({ item, propertyId, isNext, canTick, busy, onTick, uploading, onUpload, onError }: {
  item: ChecklistItem; propertyId: string; isNext: boolean; canTick: boolean; busy: boolean;
  onTick: (body: { done?: boolean; na?: boolean; value?: Record<string, string>; note?: string | null }) => void;
  uploading: boolean; onUpload: (file: File) => void; onError: (e: unknown) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(item.value ?? {});
  const [note, setNote] = useState(item.note ?? '');
  const [showNote, setShowNote] = useState(!!item.note);
  useEffect(() => { setValues(item.value ?? {}); setNote(item.note ?? ''); }, [item.value, item.note]);
  const locked = item.done && !!item.effect && LOCKING.includes(item.effect);
  const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(item.value ?? {}) || (note || '') !== (item.note ?? ''), [values, note, item.value, item.note]);
  const effectNote = item.effect ? EFFECT_NOTE[item.effect] : undefined;
  const url = refUrl(item.ref, propertyId);
  const state = item.done ? 'done' : item.na ? 'na' : 'todo';
  const doc = refFileUrl(item.ref);
  // An item that needs a document (the photo of the ID) cannot be ticked until it is in.
  const needsDoc = !!item.upload?.required && !doc && !item.done;
  const toggle = () => {
    if (!canTick || locked || busy || needsDoc) return;
    if (item.done) onTick({ done: false });
    else onTick({ done: true, value: item.fields?.length ? values : undefined, note: note || undefined });
  };
  return (
    <li className={`px-4 py-3 ${isNext ? 'bg-bronze-50/50' : ''}`}>
      <div className="flex items-start gap-3">
        <button type="button" onClick={toggle} disabled={!canTick || locked || busy || item.na || needsDoc}
          aria-label={item.done ? `Untick ${item.label}` : `Tick ${item.label}`} title={locked ? 'Done — this item has acted on another record and stays ticked' : needsDoc ? `${item.upload?.label ?? 'Upload the document'} first` : undefined}
          className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border transition ${state === 'done' ? 'border-emerald-500 bg-emerald-500 text-on-primary' : state === 'na' ? 'border-ink-200 bg-ink-100 text-ink-400' : needsDoc ? 'border-ink-200 bg-ink-50' : isNext ? 'border-bronze-500 bg-white' : 'border-ink-200 bg-white'} ${canTick && !locked && !item.na && !needsDoc ? 'hover:border-emerald-500' : 'cursor-default'}`}>
          {state === 'done' ? (locked ? <Lock className="h-3 w-3" /> : <Check className="h-3.5 w-3.5" />) : state === 'na' ? <Ban className="h-3 w-3" /> : null}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`text-[13px] ${state === 'na' ? 'text-ink-400 line-through' : 'text-ink-800'}`}>{item.label}</span>
            {isNext && <span className="rounded-full bg-bronze-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-bronze-700">Next</span>}
            {effectNote && !item.done && <span className="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10.5px] text-violet-700">{effectNote}</span>}
            {state === 'na' && <span className="text-[11px] text-ink-400">not needed</span>}
          </div>
          {item.hint && !item.done && <div className="mt-0.5 text-[11.5px] text-ink-400">{item.hint}</div>}
          {(item.done || item.na) && item.doneAt && (
            <div className="mt-0.5 text-[11.5px] text-ink-500">
              {item.na ? 'Marked not needed' : 'Done'} by {item.doneByName ?? 'someone'} · {when(item.doneAt)}
              {item.ref && !doc && <> · {url ? <Link to={url} className="font-medium text-bronze-600 hover:underline">{item.ref.label}</Link> : item.ref.label}</>}
            </div>
          )}
          {item.done && item.effectResult && (
            <div className={`mt-0.5 text-[11.5px] ${!item.effectResult.ok ? 'text-red-600' : item.effectResult.sent ? 'text-emerald-700' : 'text-ink-500'}`}>{item.effectResult.text}</div>
          )}
          {item.upload && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px]">
              {doc && item.ref && (
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-ink-400" />
                  <button type="button" onClick={() => void openStoredFile(doc).catch(onError)} className="min-w-0 truncate text-left text-bronze-700 hover:underline">{item.ref.label}</button>
                </span>
              )}
              {canTick && (
                <label className={`inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-white px-2 py-1 text-[11px] text-ink-600 ${uploading ? 'opacity-60' : 'cursor-pointer hover:border-ink-400'}`}>
                  <UploadIcon className="h-3 w-3" />{uploading ? 'Uploading…' : doc ? 'Replace' : (item.upload.label ?? 'Upload')}
                  <input type="file" accept="application/pdf,image/*" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
                </label>
              )}
              {needsDoc && <span className="text-ink-400">Needed before ticking · image or PDF</span>}
            </div>
          )}
          {item.fields && item.fields.length > 0 && (
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {item.fields.map((f) => (
                <label key={f.key} className="block">
                  <span className="mb-0.5 block text-[10.5px] font-medium uppercase tracking-wide text-ink-400">{f.label}</span>
                  {f.type === 'select'
                    ? <Select value={values[f.key] ?? ''} disabled={!canTick} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className="!py-1.5 !text-[12.5px]"><option value="">—</option>{f.options?.map((o) => <option key={o}>{o}</option>)}</Select>
                    : <Input type={f.type === 'date' ? 'date' : 'text'} inputMode={f.type === 'number' ? 'decimal' : undefined} value={values[f.key] ?? ''} disabled={!canTick || (locked && f.type !== 'text' && f.type !== 'number')}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className="!py-1.5 !text-[12.5px]" placeholder="—" />}
                </label>
              ))}
            </div>
          )}
          {showNote && <Input value={note} disabled={!canTick} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="mt-2 !py-1.5 !text-[12.5px]" />}
          {canTick && (
            <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11.5px]">
              {!showNote && <button type="button" className="text-ink-400 hover:text-ink-700" onClick={() => setShowNote(true)}>Add note</button>}
              {item.optional && !item.done && <button type="button" className="text-ink-400 hover:text-ink-700" onClick={() => onTick({ na: !item.na })}>{item.na ? 'Needed after all' : 'Not needed'}</button>}
              {dirty && (item.done || item.na || !item.fields?.length) && <button type="button" className="font-medium text-bronze-600 hover:underline" onClick={() => onTick({ value: item.fields?.length ? values : undefined, note: note || null })}>Save</button>}
              {dirty && !item.done && !item.na && !!item.fields?.length && <button type="button" className="font-medium text-bronze-600 hover:underline" onClick={() => onTick({ value: values, note: note || null })}>Save without ticking</button>}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
